import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import ReactTable from "react-table";
import { Button, ButtonGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popconfirm } from "antd";

import { closeCustomModal, openCustomModal } from "components/custom/modals";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";

import {
    fetchMiscChargeTypes,
    deleteMiscChargeTypeById,
} from "api/menu/master-files/misc-charges/actions";

const EditModal = React.lazy(() =>
    import("components/menu/master-files/misc-charges/edit-modal"));
const MiscChargeRatesModal = React.lazy(() =>
    import("components/menu/master-files/misc-charges/misc-charge-rates-modal"));

class MiscChargesList extends React.Component {
    constructor(props) {
        super(props);

        this.modalOpen = this.modalOpen.bind(this);
        this.modalDefaults = this.modalDefaults.bind(this);
    }

    componentDidMount () {
        this.props.fetchMiscChargeTypes(true);
    }

    delete = (id) => {
        this.props.deleteMiscChargeTypeById(id, () => {
            this.props.fetchMiscChargeTypes(true);
        });
    };

    modalOpen = (id = false) => {
        const modalObj = {
            id: `edit-modal-${id}`,
            header: `${id !== false ? "Update" : "Create"} Misc Charge Type`,
            size: "md",
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <EditModal
                        id={id}
                        onCancel={() => {
                            this.props.closeCustomModal(modalObj);
                        }}
                        onSubmit={() => {
                            this.props.closeCustomModal(modalObj);
                            this.props.fetchMiscChargeTypes(true);
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    modalDefaults = () => {
        const modalObj = {
            header: "Misc charge rates",
            modalSettings: {
                size: "sm",
            },
            children: (
                <React.Suspense fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <MiscChargeRatesModal
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSubmit={() => this.props.closeCustomModal(modalObj)}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    render () {
        const {
            miscChargeTypes,
            loading
        } = this.props;

        const classNameFlexCenter = "d-flex justify-content-center align-items-center";
        const columns = [
            {
                id: "name",
                headerClassName: "text-left",
                Header: "Name",
                accessor: "name",
            },
            {
                id: "isInactive",
                headerClassName: "text-left",
                Header: "Active?",
                accessor: original => (original.isInactive ? "No" : "Yes"),
                Cell: ({ original }) => (original.isInactive ? "No" : "Yes"),
                className: classNameFlexCenter,
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={{
                            "": "Show All",
                            YES: "Yes",
                            NO: "No"
                        }}
                    />
                )
            },
            {
                id: "isGSTApplicable",
                headerClassName: "text-left",
                Header: "Is GST Applicable?",
                accessor: original => (original.isGSTApplicable ? "Yes" : "No"),
                Cell: ({ original }) => (original.isGSTApplicable ? "Yes" : "No"),
                className: classNameFlexCenter,
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={{
                            "": "Show All",
                            YES: "Yes",
                            NO: "No"
                        }}
                    />
                )
            },
            {
                id: "isEmployeeInvoicing",
                headerClassName: "text-left",
                Header: "Is Employee Invoicing?",
                accessor: original => (original.isEmployeeInvoicing ? "Yes" : "No"),
                Cell: ({ original }) => (original.isEmployeeInvoicing ? "Yes" : "No"),
                className: classNameFlexCenter,
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={{
                            "": "Show All",
                            YES: "Yes",
                            NO: "No"
                        }}
                    />
                )
            },
            {
                id: "isCustomerBilling",
                headerClassName: "text-left",
                Header: "Is Customer Billing?",
                accessor: original => (original.isCustomerBilling ? "Yes" : "No"),
                Cell: ({ original }) => (original.isCustomerBilling ? "Yes" : "No"),
                className: classNameFlexCenter,
                Filter: ({ filter, onChange }) => (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={{
                            "": "Show All",
                            YES: "Yes",
                            NO: "No"
                        }}
                    />
                )
            },
            {
                id: "actions",
                Header: "Actions",
                className: classNameFlexCenter,
                Cell: ({ original }) => (
                    <ButtonGroup>
                        <Button size="sm" onClick={() => this.modalOpen(original.id)}>
                            <FontAwesomeIcon icon="pen" title="Update" />
                        </Button>
                        <Popconfirm
                            title={`Delete "${original.name}"?`}
                            onConfirm={() => this.delete(original.id)}
                            placement="bottomRight"
                            okText="Yes"
                            cancelText="Cancel"
                        >
                            <Button variant="danger" size="sm">
                                <FontAwesomeIcon icon="times" />
                            </Button>
                        </Popconfirm>
                    </ButtonGroup>
                ),
                filterable: false,
                width: 100
            }
        ];

        return (
            <div className="dashboard">
                <h3 className="mb-4">
                    Misc Charge Types
                </h3>

                <ReactTable
                    data={miscChargeTypes}
                    headerClassName="table-header"
                    columns={columns}
                    showPagination={false}
                    pageSize={miscChargeTypes ? Math.max(miscChargeTypes.length, 20) : 20}
                    resizable
                    className="-striped -highlight"
                    loading={loading}
                    filterable
                    defaultFilterMethod={filterCaseInsensitive}
                    defaultFiltered={[
                        {
                            id: "isInactive",
                            value: "YES"
                        }
                    ]}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false
                        }
                    ]}
                    getNoDataProps = {() => ({
                        style: {
                            zIndex: 0
                        }
                    })}
                    style={{
                        height: "calc(100vh - 200px)",
                    }}
                />

                <div className="mt-4 mb-4 text-right">
                    <Button
                        className="mr-2"
                        variant="info"
                        onClick={this.modalDefaults}
                    >
                        Change Defaults rates
                    </Button>
                    <Button
                        className="ml-2"
                        variant="primary"
                        onClick={() => this.modalOpen()}
                    >
                        Add New Misc Charge type
                    </Button>
                </div>
            </div>
        );
    }
}

MiscChargesList.propTypes = {
    fetchMiscChargeTypes: PropTypes.func.isRequired,
    deleteMiscChargeTypeById: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
    miscChargeTypes: state.miscCharges.miscChargeTypes,
    loading: state.miscCharges.loading
});

const mapStateToDispatch = dispatch => ({
    fetchMiscChargeTypes: bindActionCreators(fetchMiscChargeTypes, dispatch),
    deleteMiscChargeTypeById: bindActionCreators(deleteMiscChargeTypeById, dispatch),

    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch)
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(MiscChargesList);
